import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { useSEO } from "@/hooks/useSEO";
import { useSettings } from "@/hooks/useSettings";
import { useCategories } from "@/hooks/useCategories";
import { CATEGORIES, categoryById } from "@/data/categories";
import Breadcrumbs from "@/components/Breadcrumbs";
import CategoryCard from "@/components/CategoryCard";
import VehicleCard from "@/components/VehicleCard";
import JsonLd from "@/components/JsonLd";
import { SEO_PAGES_DATA } from "@/data/seoPages";
import { API, telHref } from "@/lib/api";

export default function SEOLanding({ pageKey }) {
  const data = SEO_PAGES_DATA[pageKey];
  const settings = useSettings();
  const meta = useCategories();
  const [available, setAvailable] = useState([]);

  useSEO({ title: data.seoTitle, description: data.seoDescription, path: data.path });

  useEffect(() => {
    if (data.live) {
      axios.get(`${API}/vehicles`).then((r) => setAvailable(r.data.filter((v) => v.status === "available"))).catch(() => {});
    }
  }, [pageKey]);

  const phone = settings.phone || "591 063 123";

  return (
    <main className="min-h-screen bg-[#0A0B0E] text-gray-200" data-testid={`seo-page-${pageKey}`}>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: data.h1,
        description: data.seoDescription,
        url: `https://taxipark.ge${data.path}`,
        isPartOf: { "@type": "WebSite", name: "taxipark.ge", url: "https://taxipark.ge/" },
      }} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <Breadcrumbs items={[{ name: "მთავარი", path: "/" }, { name: data.h1, path: data.path }]} />

        <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
            {data.h1}
          </h1>
          <p className="text-base sm:text-lg text-gray-400 leading-relaxed mt-5 max-w-3xl">{data.intro}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/apply" data-testid="seo-apply-cta" className="btn-gold text-base">
              განაცხადის დატოვება <ArrowRight className="w-5 h-5" />
            </Link>
            <a href={telHref(phone)} data-testid="seo-call-cta" className="btn-ghost text-base">
              <Phone className="w-5 h-5" /> დაგვირეკე
            </a>
          </div>
        </motion.header>

        {data.live && (
          <section className="mt-14" data-testid="seo-live-vehicles">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-8">
              თავისუფალია ახლა ({available.length})
            </h2>
            {available.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {available.map((v) => (
                  <VehicleCard key={v.id} vehicle={v} category={categoryById(v.category_id)} />
                ))}
              </div>
            ) : (
              <div className="card-lux p-10 text-center" data-testid="seo-live-empty">
                <h3 className="text-xl font-semibold text-white">ამ წუთას თავისუფალი ავტომობილი არ არის</h3>
                <p className="text-gray-400 mt-3 max-w-xl mx-auto leading-relaxed">
                  ყველა მანქანა დაკავებულია — მაგრამ ეს მალე იცვლება. დაგვირეკე ან დატოვე განაცხადი და შეგატყობინებთ, როგორც კი რამე გათავისუფლდება.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-7">
                  <a href={telHref(phone)} data-testid="seo-live-empty-call" className="btn-gold text-base"><Phone className="w-5 h-5" /> დაგვირეკე — {phone}</a>
                  <Link to="/catalog" className="btn-ghost text-base">სრული კატალოგი</Link>
                </div>
              </div>
            )}
          </section>
        )}

        <div className="mt-14 space-y-14">
          {data.sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              data-testid={`seo-section-${i}`}
            >
              <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4">{s.h2}</h2>
              {s.text && <p className="text-gray-400 leading-relaxed max-w-3xl">{s.text}</p>}
              {s.list && (
                <ul className="space-y-3 max-w-3xl">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-gray-300 leading-relaxed">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {s.categories && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                  {s.categories.map((cid) => {
                    const cat = CATEGORIES.find((c) => c.id === cid);
                    return cat ? <CategoryCard key={cid} category={cat} meta={meta[cid]} /> : null;
                  })}
                </div>
              )}
            </motion.section>
          ))}
        </div>

        {data.related && (
          <section className="mt-16 pt-10 border-t border-white/10">
            <h2 className="text-lg font-semibold text-white mb-5">ასევე იხილე</h2>
            <div className="flex flex-wrap gap-3">
              {data.related.map((rk) => {
                const r = SEO_PAGES_DATA[rk];
                return (
                  <Link key={rk} to={r.path} data-testid={`seo-related-${rk}`} className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-gray-300 hover:border-[#D4AF37]/50 hover:text-[#E5C378] transition-colors">
                    {r.h1}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <section className="mt-16 card-lux p-8 sm:p-10 text-center" data-testid="seo-final-cta">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white">მზად ხარ დაიწყო?</h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">
            დატოვე განაცხადი ან დაგვირეკე — ავტომობილი შეგიძლია დღესვე აიღო.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-7">
            <Link to="/apply" className="btn-gold text-base">განაცხადის დატოვება <ArrowRight className="w-5 h-5" /></Link>
            <a href={telHref(phone)} className="btn-ghost text-base"><Phone className="w-5 h-5" /> {phone}</a>
          </div>
        </section>
      </div>
    </main>
  );
}
